// Root application component.
// Defines all client-side routes and wraps the app in a BrowserRouter.
// Every page is lazily loaded so the initial bundle stays small.
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import FloatingActions from "./components/FloatingActions";
import GlobalIncomingCallBanner from "./components/GlobalIncomingCallBanner";
import GlobalBookingNotifications from "./components/GlobalBookingNotifications";
import ScrollToTop from "./components/ScrollToTop";
import PageLoader from "./components/PageLoader";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { Toaster } from "react-hot-toast";

// All pages are loaded lazily (code-splitting) so the browser only downloads
// what the user actually navigates to — improves initial load time significantly.
const Home = lazy(() => import("./pages/HomeRedesign"));
const Lawyers = lazy(() => import("./pages/Lawyers"));
const LawyerProfile = lazy(() => import("./pages/LawyerProfile"));
const LawyerProfileSubmit = lazy(() => import("./pages/LawyerProfileSubmit"));
const CaseManagement = lazy(() => import("./pages/CaseManagement"));
const AgentCRM = lazy(() => import("./pages/AgentCRM"));
const AgentDocuments = lazy(() => import("./pages/AgentDocuments"));
const AgentCalendar = lazy(() => import("./pages/AgentCalendar"));
const AgentBilling = lazy(() => import("./pages/AgentBilling"));
const AgentPricing = lazy(() => import("./pages/AgentPricing"));
const AgentRatings = lazy(() => import("./pages/AgentRatings"));
const AgentBookings = lazy(() => import("./pages/AgentBookings"));
const AgentNotifications = lazy(() => import("./pages/AgentNotifications"));
const AgentSupport = lazy(() => import("./pages/AgentSupport"));
const ChatRoom = lazy(() => import("./pages/ChatRoom"));
const AgentChatRoom = lazy(() => import("./pages/AgentChatRoom"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Register = lazy(() => import("./pages/Register"));
const PracticeAreas = lazy(() => import("./pages/PracticeAreas"));
const CourtDates = lazy(() => import("./pages/CourtDates"));
const SavedAgents = lazy(() => import("./pages/SavedAgents"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const LegalGuides = lazy(() => import("./pages/LegalGuides"));
const LegalDocuments = lazy(() => import("./pages/LegalDocuments"));
const LegalPolicy = lazy(() => import("./pages/LegalPolicy"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

const MyBookings = lazy(() => import("./pages/MyBookings"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Wallet = lazy(() => import("./pages/Wallet"));
const Support = lazy(() => import("./pages/Support"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminWalletManagement = lazy(
  () => import("./pages/AdminWalletManagement"),
);

// App is the single entry point for routing.
// FloatingActions and ScrollToTop sit outside <Routes> so they persist on every page.
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster
            position="top-center"
            reverseOrder={false}
            gutter={12}
            containerStyle={{
              top: 28,
              zIndex: 99999999,
            }}
            toastOptions={{
              duration: 4000,
              style: {
                background: "rgba(15, 23, 42, 0.95)",
                color: "#f8fafc",
                border: "1px solid rgba(201, 168, 76, 0.3)",
                boxShadow:
                  "0 20px 40px -10px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.08)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                borderRadius: "14px",
                padding: "12px 20px",
                fontSize: "14px",
                fontWeight: "600",
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                maxWidth: "480px",
                letterSpacing: "0.1px",
              },
              success: {
                style: {
                  background: "rgba(12, 28, 22, 0.96)",
                  border: "1px solid rgba(16, 185, 129, 0.45)",
                  color: "#f0fdf4",
                },
                iconTheme: {
                  primary: "#10b981",
                  secondary: "#ffffff",
                },
              },
              error: {
                style: {
                  background: "rgba(28, 16, 20, 0.96)",
                  border: "1px solid rgba(239, 68, 68, 0.45)",
                  color: "#fef2f2",
                },
                iconTheme: {
                  primary: "#ef4444",
                  secondary: "#ffffff",
                },
              },
            }}
          />
          <GlobalIncomingCallBanner />
          <GlobalBookingNotifications />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/lawyers" element={<Lawyers />} />
              <Route path="/lawyer/:id" element={<LawyerProfile />} />
              <Route path="/practice-areas" element={<PracticeAreas />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/legal-guides" element={<LegalGuides />} />
              <Route path="/legal-documents" element={<LegalDocuments />} />
              <Route path="/register" element={<Register />} />
              <Route path="/terms" element={<LegalPolicy />} />
              <Route path="/privacy" element={<LegalPolicy />} />
              <Route path="/refund" element={<LegalPolicy />} />
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Role-Protected Lawyer/Agent Routes */}
              <Route
                path="/agent-dashboard"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <Dashboard defaultView="agent" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/profile-submit"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <LawyerProfileSubmit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/cases"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <CaseManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/crm"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentCRM />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/documents"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentDocuments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/calendar"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/billing"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentBilling />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/pricing"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentPricing />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agent-pricing"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentPricing />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agent/pricing"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentPricing />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/ratings"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentRatings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/bookings"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentBookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/notifications"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentNotifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/support"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentSupport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agent-chat/:id"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentChatRoom />
                  </ProtectedRoute>
                }
              />
              {/* No session id means there's no conversation to resolve — send
                the lawyer to their real conversation list instead of a blank,
                broken chat screen. */}
              <Route
                path="/agent-chat"
                element={<Navigate to="/lawyer/crm" replace />}
              />
              <Route
                path="/lawyer/chat/:id"
                element={
                  <ProtectedRoute allowedRoles={["agent", "lawyer"]}>
                    <AgentChatRoom />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lawyer/chat"
                element={<Navigate to="/lawyer/crm" replace />}
              />

              {/* Authenticated Customer & Universal Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "agent", "lawyer", "admin"]}
                  >
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat/:id"
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "agent", "lawyer", "admin"]}
                  >
                    <ChatRoom />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/court-dates"
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "agent", "lawyer", "admin"]}
                  >
                    <CourtDates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings"
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "agent", "lawyer", "admin"]}
                  >
                    <MyBookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/saved-agents"
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "agent", "lawyer", "admin"]}
                  >
                    <SavedAgents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile-settings"
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "agent", "lawyer", "admin"]}
                  >
                    <ProfileSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "agent", "lawyer", "admin"]}
                  >
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wallet"
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "agent", "lawyer", "admin"]}
                  >
                    <Wallet defaultTab="wallet" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders"
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "agent", "lawyer", "admin"]}
                  >
                    <Wallet defaultTab="orders" />
                  </ProtectedRoute>
                }
              />
              <Route path="/support" element={<Support />} />

              {/* Role-Protected Admin Routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute
                    allowedRoles={["admin", "super admin", "super_admin"]}
                  >
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/wallet-management"
                element={
                  <ProtectedRoute
                    allowedRoles={["admin", "super admin", "super_admin"]}
                  >
                    <AdminWalletManagement />
                  </ProtectedRoute>
                }
              />

              {/* Catch-all route — any unknown URL redirects back to the homepage. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <FloatingActions />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
